import React, { useEffect, useRef, useCallback } from 'react';
import './ResumeTree.css';
import { FaEnvelope, FaPhone, FaLinkedin, FaGithub } from 'react-icons/fa';

const ResumeTree = ({ data }) => {
  const nodeIdCounterRef = useRef(0);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const nodePositionsRef = useRef(new Map());

  const iconMap = {
    FaEnvelope,
    FaPhone,
    FaLinkedin,
    FaGithub
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent style={{ marginRight: '8px', color: '#888' }} /> : null;
  };

  const drawConnections = () => {
    if (!svgRef.current || !containerRef.current) return;

    const svg = svgRef.current;
    const container = containerRef.current;

    // Clear existing lines
    while (svg.firstChild) {
      svg.removeChild(svg.firstChild);
    }

    // Get all visible nodes
    const nodes = container.querySelectorAll('.tree-node');
    nodePositionsRef.current.clear();

    // Store positions using unique IDs
    nodes.forEach(node => {
      const rect = node.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if mobile
      const isMobile = window.innerWidth <= 768;

      if (isMobile) {
        nodePositionsRef.current.set(node.dataset.nodeId, {
          x: rect.left - containerRect.left,
          yTop: rect.top - containerRect.top,
          yMiddle: rect.top - containerRect.top + rect.height / 2,
          yBottom: rect.top - containerRect.top + rect.height,
          element: node
        });
      } else {
        nodePositionsRef.current.set(node.dataset.nodeId, {
          xRight: rect.left - containerRect.left + rect.width,
          xLeft: rect.left - containerRect.left,
          y: rect.top - containerRect.top + rect.height / 2,
          element: node
        });
      }
    });

    // Draw connections based on device
    const isMobile = window.innerWidth <= 768;

    nodes.forEach(node => {
      const wrapper = node.closest('.node-wrapper');
      const childrenContainer = wrapper?.querySelector(':scope > .children-container:not(.hidden)');
      if (childrenContainer) {
        const parentPos = nodePositionsRef.current.get(node.dataset.nodeId);
        const children = childrenContainer.querySelectorAll(':scope > .node-wrapper > .tree-node');

        if (isMobile) {
          // Mobile: Straight vertical connections on the left side (GitHub commit style)
          if (children.length > 0 && parentPos) {
            const childPositions = Array.from(children)
              .map(child => nodePositionsRef.current.get(child.dataset.nodeId))
              .filter(pos => pos);

            if (childPositions.length > 0) {
              const lastChild = childPositions[childPositions.length - 1];

              // Get hierarchy level from data-level attribute
              const parentLevel = parseInt(node.dataset.level) || 0;
              const lineX = 15 + (parentLevel * 12); // Shift right for each level
              const strokeWidth = 2 + (parentLevel * 0.4); // Thicker for deeper hierarchy

              // Draw vertical line on the left connecting parent to all children
              const verticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              verticalLine.setAttribute('x1', lineX);
              verticalLine.setAttribute('y1', parentPos.yMiddle);
              verticalLine.setAttribute('x2', lineX);
              verticalLine.setAttribute('y2', lastChild.yMiddle);
              verticalLine.setAttribute('stroke', '#2196F3');
              verticalLine.setAttribute('stroke-width', strokeWidth);
              verticalLine.setAttribute('stroke-linecap', 'round');
              verticalLine.setAttribute('stroke-linejoin', 'round');
              verticalLine.setAttribute('fill', 'none');
              svg.appendChild(verticalLine);

              // Draw straight horizontal line from parent to vertical line
              const parentLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              parentLine.setAttribute('x1', lineX);
              parentLine.setAttribute('y1', parentPos.yMiddle);
              parentLine.setAttribute('x2', parentPos.x);
              parentLine.setAttribute('y2', parentPos.yMiddle);
              parentLine.setAttribute('stroke', '#2196F3');
              parentLine.setAttribute('stroke-width', strokeWidth);
              parentLine.setAttribute('stroke-linecap', 'round');
              parentLine.setAttribute('stroke-linejoin', 'round');
              parentLine.setAttribute('fill', 'none');
              svg.appendChild(parentLine);

              // Draw straight horizontal lines from vertical line to each child
              childPositions.forEach((childPos, index) => {
                const childLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
                childLine.setAttribute('x1', lineX);
                childLine.setAttribute('y1', childPos.yMiddle);
                childLine.setAttribute('x2', childPos.x);
                childLine.setAttribute('y2', childPos.yMiddle);
                childLine.setAttribute('stroke', '#2196F3');
                childLine.setAttribute('stroke-width', strokeWidth);
                childLine.setAttribute('stroke-linecap', 'round');
                childLine.setAttribute('stroke-linejoin', 'round');
                childLine.setAttribute('fill', 'none');
                svg.appendChild(childLine);
              });
            }
          }
        } else {
          // Desktop: Horizontal curved connections (left-to-right tree)
          children.forEach(child => {
            const childPos = nodePositionsRef.current.get(child.dataset.nodeId);

            if (parentPos && childPos) {
              const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');

              // Create horizontal-then-vertical path (left-to-right tree)
              // From parent's right edge to child's left edge
              const midX = (parentPos.xRight + childPos.xLeft) / 2;
              const d = `M ${parentPos.xRight} ${parentPos.y}
                        C ${midX} ${parentPos.y},
                          ${midX} ${childPos.y},
                          ${childPos.xLeft} ${childPos.y}`;

              path.setAttribute('d', d);
              path.setAttribute('class', node.classList.contains('expanded') ? 'edge active' : 'edge');
              svg.appendChild(path);
            }
          });
        }
      }
    });

    // Adjust SVG size to accommodate horizontal layout
    const treeElement = container.querySelector('#tree');
    if (treeElement) {
      const treeRect = treeElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      svg.setAttribute('width', Math.max(treeRect.width, containerRect.width));
      svg.setAttribute('height', Math.max(treeRect.height, containerRect.height));
    }
  };

  const expandAll = () => {
    if (!containerRef.current) return;

    containerRef.current.querySelectorAll('.children-container').forEach(c => {
      c.classList.remove('hidden');
    });
    containerRef.current.querySelectorAll('.tree-node').forEach(n => {
      const hasChildren = n.closest('.node-wrapper')?.querySelector('.children-container');
      if (hasChildren) {
        n.classList.add('expanded');
      }
    });
    setTimeout(drawConnections, 100);
  };

  const collapseAll = useCallback(() => {
    if (!containerRef.current) return;

    containerRef.current.querySelectorAll('.children-container').forEach(c => {
      c.classList.add('hidden');
    });
    containerRef.current.querySelectorAll('.tree-node').forEach(n => {
      n.classList.remove('expanded');
    });
    setTimeout(drawConnections, 100);
  }, []);

  const viewCV = () => {
    window.open('https://docs.google.com/document/d/1-4nvDbCTHQMkgRI6Ay0Dm5OQtqNeTiKE/edit', '_blank');
  };

  const collapseDescendants = (container) => {
    // Recursively collapse all descendant nodes
    const allDescendantNodes = container.querySelectorAll('.tree-node');
    const allDescendantContainers = container.querySelectorAll('.children-container');

    allDescendantNodes.forEach(n => n.classList.remove('expanded'));
    allDescendantContainers.forEach(c => c.classList.add('hidden'));
  };

  const getNodePath = (node) => {
    const path = [];
    let currentNode = node;

    while (currentNode && currentNode.classList.contains('tree-node')) {
      const nodeName = currentNode.dataset.name;
      if (nodeName) {
        path.unshift(nodeName);
      }
      const wrapper = currentNode.closest('.node-wrapper');
      const parentContainer = wrapper?.parentElement;
      if (parentContainer && parentContainer.classList.contains('children-container')) {
        const parentWrapper = parentContainer.parentElement?.closest('.node-wrapper');
        currentNode = parentWrapper?.querySelector(':scope > .tree-node');
      } else {
        break;
      }
    }
    return path;
  };

  const handleNodeClick = (e) => {
    const node = e.target;
    if (!node.classList.contains('tree-node')) return;

    // Check if node has a URL - if so, open it
    const nodeUrl = node.dataset.url;
    if (nodeUrl) {
      window.open(nodeUrl, '_blank');
      return;
    }

    const wrapper = node.closest('.node-wrapper');
    const childrenContainer = wrapper?.querySelector(':scope > .children-container');

    // Update URL with full hierarchy path
    const nodePath = getNodePath(node);
    const urlFriendlyPath = nodePath
      .map(name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
      .join('/');
    window.history.pushState({}, '', `#${urlFriendlyPath}`);

    if (childrenContainer) {
      e.stopPropagation();
      e.preventDefault();

      // If we're expanding this node, collapse all siblings
      const isCurrentlyCollapsed = childrenContainer.classList.contains('hidden');
      if (isCurrentlyCollapsed) {
        // Find parent's children container to get siblings
        const parentChildrenContainer = wrapper.parentElement;
        if (parentChildrenContainer && parentChildrenContainer.classList.contains('children-container')) {
          // Collapse all sibling nodes and their descendants
          const siblingWrappers = parentChildrenContainer.querySelectorAll(':scope > .node-wrapper');
          siblingWrappers.forEach(siblingWrapper => {
            if (siblingWrapper !== wrapper) {
              const siblingNode = siblingWrapper.querySelector(':scope > .tree-node');
              const siblingChildren = siblingWrapper.querySelector(':scope > .children-container');
              if (siblingChildren) {
                siblingChildren.classList.add('hidden');
                siblingNode?.classList.remove('expanded');
                // Collapse all descendants of the sibling
                collapseDescendants(siblingChildren);
              }
            }
          });
        }
      } else {
        // If we're collapsing this node, collapse all its descendants
        collapseDescendants(childrenContainer);
      }

      // Toggle the clicked node
      childrenContainer.classList.toggle('hidden');
      node.classList.toggle('expanded');

      // Center the clicked node
      setTimeout(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 50);

      setTimeout(drawConnections, 100);
    }
  };

  const TreeNode = ({ data, level = 0 }) => {
    const nodeIdRef = useRef(`node-${nodeIdCounterRef.current++}`);
    const isLeaf = !data.children || data.children.length === 0;
    const isLongText = isLeaf && data.name && data.name.length > 80;

    return (
      <div className="node-wrapper">
        <div
          className={`tree-node${isLongText ? ' long-text-node' : ''}`}
          data-level={level}
          data-name={data.name}
          data-node-id={nodeIdRef.current}
          data-url={data.url || ''}
          onClick={handleNodeClick}
          style={{ cursor: data.url ? 'pointer' : 'pointer', display: 'flex', alignItems: 'center' }}
        >
          {data.icon && getIcon(data.icon)}
          {isLongText ? <p style={{ margin: 0 }}>{data.name}</p> : data.name}
        </div>
        {data.children && data.children.length > 0 && (
          <div className="children-container hidden">
            {data.children.map((child, index) => (
              <TreeNode key={index} data={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const expandNodeByHash = useCallback(() => {
    if (!containerRef.current) return;

    const hash = window.location.hash.substring(1);
    if (!hash) {
      return;
    }

    // Find node matching the hash path
    const nodes = containerRef.current.querySelectorAll('.tree-node');
    for (const node of nodes) {
      const nodePath = getNodePath(node);
      const urlFriendlyPath = nodePath
        .map(name => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''))
        .join('/');

      if (urlFriendlyPath === hash) {
        // Expand all parent nodes up the tree
        let currentElement = node;
        let wrapper = currentElement.closest('.node-wrapper');
        let parentChildrenContainer = wrapper?.closest('.children-container');

        while (parentChildrenContainer) {
          parentChildrenContainer.classList.remove('hidden');
          const parentWrapper = parentChildrenContainer.parentElement?.closest('.node-wrapper');
          const parentNode = parentWrapper?.querySelector(':scope > .tree-node');
          if (parentNode) {
            parentNode.classList.add('expanded');
          }
          parentChildrenContainer = parentWrapper?.closest('.children-container');
        }

        // Expand the target node itself if it has children
        const targetWrapper = node.closest('.node-wrapper');
        const targetChildrenContainer = targetWrapper?.querySelector(':scope > .children-container');
        if (targetChildrenContainer) {
          targetChildrenContainer.classList.remove('hidden');
          node.classList.add('expanded');
        }

        // Scroll to the node
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(drawConnections, 100);
        break;
      }
    }
  }, []);

  useEffect(() => {
    // Redirects the URL to include /# on load
    window.location.hash = '#luciano-crossa-niell/about';
  }, []); // Empty dependency array ensures this only runs once when the component mounts


  useEffect(() => {
    // Initial expansion based on URL hash
    setTimeout(expandNodeByHash, 100);

    // Handle browser back/forward
    const handleHashChange = () => {
      collapseAll();
      setTimeout(expandNodeByHash, 150);
    };

    // Redraw on resize
    const handleResize = () => {
      setTimeout(drawConnections, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [collapseAll, expandNodeByHash]);

  return (
    <div className="resume-tree-container">
      <div className="controls">
        <button onClick={expandAll}>Expand All</button>
        <button onClick={collapseAll}>Collapse All</button>
        <button onClick={viewCV}>View CV</button>
      </div>

      <div className="tree-container" ref={containerRef}>
        <svg ref={svgRef} id="connections"></svg>
        <div id="tree">
          <TreeNode data={data} />
        </div>
      </div>
    </div>
  );
};

export default ResumeTree;
